import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { API_ACCOUNT_SIGNATURE_DATA } from 'api/endpoints';

import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CircularProgress } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import SystemUpdateIcon from '@material-ui/icons/SystemUpdate';
import CheckIcon from '@material-ui/icons/Check';
import Divider from '@material-ui/core/Divider';

import compose from 'helpers/compose';
import MaterialTable from 'components/Table/Table';
import Background from 'components/Layout/Background';
import ControlButtons from 'components/ControlButtons';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { RootStore } from 'stores/rootStore';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { DocumentsStore } from 'stores/account/documentsStore';
//import { DetailSection, DetailPage, Flows } from '../enums';
import { AccountStatus, EdwardColors } from 'enums';
import { Permission } from 'helpers/permissions';
import useStyles from 'pages/Accounts/Detail/styles';

interface Props {
  rootStore: RootStore;
  accountDetailStore: AccountDetailStore;
}
interface SignatureData {
  phone: string;
  email: string;
}

const Documents: React.FC<Props> = ({ rootStore, accountDetailStore }) => {
  const { status, accountId } = accountDetailStore;
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [signatureData, setSignatureData] = useState<SignatureData | undefined>(undefined);
  const { t } = useTranslation();
  const [store] = useState(new DocumentsStore(rootStore, accountDetailStore.flow!.id, accountId));
  const { uiStore, apiStore, userStore } = rootStore.stores;

  const classes = useStyles({});
  const mobile = useMediaQuery('(max-width:400px)', { noSsr: true });

  const iqPresent = store.data && store.data.some((doc) => doc.type === 'INVESTMENT_QUESTIONNAIRE');
  const docSuffix = status === AccountStatus.AWAITING_APPROVAL ? '_03' : '';

  useEffect(() => {
    store.fetchData();
  }, [store]);

  useEffect(() => {
    (async () => {
      uiStore && uiStore.setLoading(true);
      const response = await apiStore.get<SignatureData>(API_ACCOUNT_SIGNATURE_DATA(accountId));
      response && setSignatureData(response.data);
      uiStore && uiStore.setLoading(false);
    })();
  }, [accountId, apiStore, uiStore]);

  const handleNext = async () => {
    setIsConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    await store.sendDocuments(accountDetailStore);
  };

  return store.data ? (
    <>
      <ConfirmDialog
        title={`general_confirm`}
        isOpen={isConfirmDialogOpen}
        close={() => setIsConfirmDialogOpen(false)}
        confirm={handleConfirm}
        btnCloseText="accountDetail_cancelButton"
        btnConfirmText="accountDetail_confirmButton"
      >
        {iqPresent && (
          <>
            <Divider />
            <Typography style={{ paddingLeft: '48px', position: 'relative', margin: '8px 0' }}>
              <CheckIcon
                htmlColor={EdwardColors.DARK}
                style={{ position: 'absolute', left: 0, top: '50%', transform: 'translate(0,-50%)' }}
                fontSize="large"
              />
              {t('accountDetail_confirmContent_accOverdue')}
            </Typography>
            <Divider />
          </>
        )}
        <div
          dangerouslySetInnerHTML={{
            __html: t(`accountDetail_confirmContent`, {
              email: signatureData?.email,
              phone: signatureData?.phone,
              interpolation: { escapeValue: false },
            }),
          }}
        />
      </ConfirmDialog>

      <Container maxWidth="md">
        <Background>
          <Box marginBottom={4} marginTop={2}>
            <Typography variant="body2">{t('accountDetail_documents_info')}</Typography>
          </Box>
          <Typography variant="h2">{t('accountDetail_documents_title')}</Typography>
          <MaterialTable
            options={{
              header: false,
              sorting: false,
              draggable: false,
              search: false,
              toolbar: false,
              paging: false,
            }}
            data={store.data}
            columns={[
              { field: 'type', render: ({ type }) => t(`pdf_document_${type}${docSuffix}`) },
              {
                field: 'template',
                render: (document) => (
                  <Button
                    variant={mobile ? 'text' : 'outlined'}
                    color={mobile ? 'primary' : 'default'}
                    className={classes.downloadButton}
                    startIcon={
                      document.loading ? <CircularProgress size={20} /> : mobile ? <SystemUpdateIcon /> : <GetAppIcon />
                    }
                    onClick={() => store.downloadDocument(document)}
                  >
                    {!mobile && t('general_download')}
                  </Button>
                ),
                cellStyle: {
                  width: mobile ? '5%' : '10%',
                },
              },
            ]}
          />
        </Background>
      </Container>

      <ControlButtons
        isSubmitting={false}
        backText={t(accountDetailStore.prevStepLabel)}
        onBack={() => accountDetailStore.goBack()}
        nextText={t('accountDetail_send')}
        nextDisabled={!userStore.checkPermissions(Permission.ACCOUNT_REQUEST_SIGN)}
        onNext={handleNext}
        hideBack={accountDetailStore.firstStep}
      />
    </>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Documents);
